import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiChevronRight, mdiLoading } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { AppIcon } from '../ui/icon'
import { Card } from '../ui/card'
import { useAssetTypes } from '../../api/queries'
import { AssetTypeTile } from '../assets/AssetTypeTile'
import { STEERING_CATEGORIES, CATEGORY_VISUALS, categoryLabelKey, type SteeringCategory } from '../steering/steering-categories'

/**
 * The assets carrying the most work, ranked by urgency (spec 2026-08-24-03,
 * lot 11).
 *
 * This is the half of the home page that Steering cannot do: Steering makes you
 * pick a category first, so nobody ever sees "where is the work, across all
 * three". The cards above answer *how much and of what kind*; this answers
 * *where*, and its rows carry the same three-category split so the two halves
 * read as one thought.
 *
 * Grouping is on the **root asset, whatever its type** — a building group here,
 * a building or a site elsewhere. Hardcoding `building` would have produced an
 * empty block for an organisation that models land, parking or a portfolio of
 * lots; on the demo org alone the roots are building groups, a building and a
 * unit.
 *
 * Every count is the server's. Each row links into Steering scoped to that
 * asset's whole branch — `asset_subtree_uid`, not `asset_uid`, or the queue
 * would contradict the number just clicked (the roll-up counts descendants; an
 * exact-match filter returns the tickets targeting the root itself, which on
 * real data is usually none).
 */

/** Steering's three, never data quality — that is not the manager's work and
 * lives in the Data section. */
const STEERING_CATEGORY_FILTER = STEERING_CATEGORIES.join(',')

const ROW_LIMIT = 6

type StatRow = {
    count?: number
    asset_uid?: string | null
    asset_name?: string | null
    asset_type_code?: string | null
    category?: string | null
}

type AssetRow = {
    uid: string
    /** The category holding most of this asset's work — where the row links.
     * Landing on the default category would open an empty queue whenever an
     * asset's work sits elsewhere, which on real data is the common case. */
    topCategory: SteeringCategory
    name: string
    typeCode: string
    total: number
    urgent: number
    byCategory: Record<string, number>
}

export function CriticalAssetsPanel({ org, status }: { org: string; status: string }) {
    const { t } = useTranslation(['issues', 'assets'])

    const query = { status, assignee_uid: 'all', category: STEERING_CATEGORY_FILTER }

    // The stats rows carry a type CODE, not a type: the tile needs the type
    // itself to read its icon and its family colour.
    const { data: assetTypes } = useAssetTypes(org)
    const assetTypeByCode = useMemo(
        () => new Map((assetTypes ?? []).map((t) => [t.code, t])),
        [assetTypes]
    )

    // One call for the split, one for the urgent slice — the same shape the
    // category cards use.
    const splitStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: { path: { orgCode: org }, query: { ...query, group_by: 'root_asset,category' } },
    })
    const urgentStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: { path: { orgCode: org }, query: { ...query, group_by: 'root_asset', priority: '1,2' } },
    })

    const rows = useMemo<AssetRow[]>(() => {
        const urgentByUID: Record<string, number> = {}
        for (const r of (urgentStats.data?.data ?? []) as StatRow[]) {
            if (r.asset_uid && typeof r.count === 'number') urgentByUID[r.asset_uid] = r.count
        }

        const byUID: Record<string, AssetRow> = {}
        for (const r of (splitStats.data?.data ?? []) as StatRow[]) {
            if (!r.asset_uid || typeof r.count !== 'number') continue
            const row = (byUID[r.asset_uid] ??= {
                uid: r.asset_uid,
                name: r.asset_name ?? '',
                typeCode: r.asset_type_code ?? '',
                topCategory: STEERING_CATEGORIES[0],
                total: 0,
                urgent: urgentByUID[r.asset_uid] ?? 0,
                byCategory: {},
            })
            row.total += r.count
            if (r.category) row.byCategory[r.category] = (row.byCategory[r.category] ?? 0) + r.count
        }

        // Urgent first, then volume — an asset with three emergencies outranks
        // one with forty trivia.
        for (const row of Object.values(byUID)) {
            row.topCategory = STEERING_CATEGORIES.reduce((best, c) =>
                (row.byCategory[c] ?? 0) > (row.byCategory[best] ?? 0) ? c : best, STEERING_CATEGORIES[0])
        }

        return Object.values(byUID)
            .sort((a, b) => b.urgent - a.urgent || b.total - a.total)
            .slice(0, ROW_LIMIT)
    }, [splitStats.data, urgentStats.data])

    const loading = splitStats.isLoading || urgentStats.isLoading

    if (!loading && rows.length === 0) return null

    return (
        <div className="mb-6" data-testid="critical-assets">
            <h2 className="text-lg font-semibold text-foreground mb-3">{t('issues:critical_assets.title')}</h2>
            <p className="text-sm text-muted-foreground mb-3">{t('issues:critical_assets.subtitle')}</p>

            {loading ? (
                <Card className="flex items-center justify-center py-10">
                    <AppIcon path={mdiLoading} size="md" className="motion-safe:animate-spin" />
                </Card>
            ) : (
                <Card className="divide-y overflow-hidden">
                    {rows.map((row) => (
                        <Link
                            key={row.uid}
                            to="/$org/steering"
                            params={{ org }}
                            search={{
                                category: row.topCategory,
                                asset: row.uid,
                                view: undefined,
                                selected: undefined,
                                subcategory: undefined,
                                subtree: undefined,
                                due: undefined,
                                status: undefined,
                                sort: undefined,
                                q: undefined,
                                page: undefined,
                                tab: undefined,
                                nature: undefined,
                            }}
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors"
                            data-testid={`critical-asset-${row.uid}`}
                        >
                            <AssetTypeTile type={assetTypeByCode.get(row.typeCode)} size="sm" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-foreground truncate">{row.name}</div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {STEERING_CATEGORIES.map((c) => {
                                        const n = row.byCategory[c] ?? 0
                                        if (n === 0) return null
                                        const { icon, text } = CATEGORY_VISUALS[c]
                                        return (
                                            <span
                                                key={c}
                                                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums"
                                                title={t(categoryLabelKey(c))}
                                                data-testid={`critical-asset-${row.uid}-${c}`}
                                            >
                                                <AppIcon path={icon} size="xs" className={text} />
                                                {n.toLocaleString()}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                            {row.urgent > 0 && row.urgent < row.total && (
                                <span
                                    className="text-[11px] text-warning tabular-nums flex-shrink-0"
                                    data-testid={`critical-asset-${row.uid}-urgent`}
                                >
                                    {t('issues:steering.category_urgent', { count: row.urgent })}
                                </span>
                            )}
                            <span className="text-sm font-medium text-foreground tabular-nums flex-shrink-0 w-12 text-right">
                                {row.total.toLocaleString()}
                            </span>
                            <AppIcon path={mdiChevronRight} size="sm" tone="muted" className="flex-shrink-0" />
                        </Link>
                    ))}
                </Card>
            )}
        </div>
    )
}
